import { Component, DestroyRef, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { maskCpfAndCnpj, maskOdometer, maskZipCode } from 'src/assets/functions/information-masks';
import { ApsPermitedScreen } from '../../models/aps-permission/aps-permited-screen';
import { ChoosePlanBuyng } from '../../models/choose-plan/choose-plan-buyng';
import { ClientData } from '../../models/client-data/client-data';
import { ConstantsFordProtect } from '../../models/constants/constants-ford-protect';
import { ContractSummary } from '../../models/contract-summary/contract-summary';
import { ContractFull } from '../../models/contract/contract-full';
import { DownloadFile } from '../../models/contract/download-file';
import { DealerCountryDto } from '../../models/dealer/dealer-country-dto';
import { FileType } from '../../models/enum/file-type';
import { OperationCode } from '../../models/enum/operation-code';
import { SaleChannel } from '../../models/enum/sale-channel';
import { SalesTypeFordEnum } from '../../models/enum/sales-type-ford';
import { StatusChassi } from '../../models/enum/status-chassi';
import { ProductFull } from '../../models/product/product-full';
import { VinValidate } from "../../models/vin-validate/vin-validate";
import { ApsPermissionService } from '../../services/aps-permission/aps-permission.service';
import { ChoosePlanService } from '../../services/choose-plan/choose-plan.service';
import { ClientDataService } from '../../services/client-data/client-data.service';
import { ContractSummaryService } from '../../services/contract-summary/contract-summary.service';
import { ContractSignatureDraftService } from '../../services/contract/contract-signature-draft.service';
import { ContractService } from '../../services/contract/contract.service';
import { DealerService } from '../../services/dealer/dealer.service';
import { I18nBrokerService } from '../../services/i18n/i18n-broker.service';
import { ImageService } from '../../services/image/image.service';
import { MessageExceptionService } from '../../services/message-exception/message-exception.service';
import { ValidationErrorMapperService } from '../../services/address/validation-error-mapper.service';
import { SystemCountryService } from '../../services/system-country/system-country.service';
import { VehicleInformationService } from '../../services/vehicle-information/vehicle-information.service';
import { SignaturePadComponent } from '../../shared/components/signature-pad/signature-pad.component';
import { nationalId } from 'src/assets/validations/cpf_cnpj_validation';

@Component({
	selector: 'app-contract-summary-attachment-two',
	templateUrl: './contract-summary-attachment-two.component.html',
	styleUrls: ['./contract-summary-attachment-two.component.scss'],
	standalone: false
})
export class ContractSummaryAttachmentTwoComponent implements OnInit {
	private readonly destroyRef = inject(DestroyRef);

	isSubmitted = false;
	blockedUi = false;
	isDownloadingContract = false;
	isContractCreated = false;
	contractIdDownload = 0;

	contractForm!: UntypedFormGroup;
	invalidSignature = true;
	isCtaSuccess = false;
	signatureImgBase64!: string;
	vinValidate!: VinValidate;
	clientData!: ClientData;
	contractSummary!: ContractSummary;
	termsUser = false;
	dateAttachment = new Date();
	choosePlanBuyng!: ChoosePlanBuyng;
	saleChannel!: SaleChannel;
	dealerCountry!: DealerCountryDto;
	lang!: string;
	systemCountry!: string;

	@ViewChild('signaturePad') signaturePadCmp?: SignaturePadComponent;
	@ViewChild('termsConsent', { read: ElementRef }) termsConsentRef?: ElementRef<HTMLElement>;
	submitted = false;
	isDrawing = false;
	termsFocusActive = false;
	readonly maskCpfAndCnpj = maskCpfAndCnpj;
	readonly maskZipCode = maskZipCode;
	readonly maskOdometer = maskOdometer;

	constructor(private contractSummaryService: ContractSummaryService,
		private vehicleInformationService: VehicleInformationService,
		private clientDataService: ClientDataService,
		private translateService: TranslateService,
		private messageService: MessageService,
		private router: Router,
		private route: ActivatedRoute,
		private fb: UntypedFormBuilder,
		private apsPermissionService: ApsPermissionService,
		private choosePlanService: ChoosePlanService,
		private messageExceptionService: MessageExceptionService,
		private contractService: ContractService,
		private dealerService: DealerService,
		private systemCountryService: SystemCountryService,
		private imageService: ImageService,
		private i18nBroker: I18nBrokerService,
		private validationErrorMapperService: ValidationErrorMapperService,
		private contractSignatureDraftService: ContractSignatureDraftService
	) {

		this.choosePlanBuyng = new ChoosePlanBuyng;
		this.saleChannel = route.snapshot.paramMap.get('saleChannel') as SaleChannel;
		this.systemCountryService.setSystemCountry();
	}

	ngOnInit() {

		window.scrollTo(0, 0);

		this.vinValidate = this.vehicleInformationService.getAllChassiValidation();
		this.clientData = this.clientDataService.getClientData();
		this.contractSummary = this.contractSummaryService.getContractSummary();

		if (this.vinValidate === null || this.vinValidate.validated === StatusChassi.invalid ||
			this.clientDataService.getClientData() === null || this.clientData === null ||
			this.contractSummary === null) {
			this.router.navigate(['sales-channel', this.saleChannel], { skipLocationChange: true, replaceUrl: false });
		}

		this.apsPermissionService.permissionValidation(ApsPermitedScreen.screenSignature);

		if (this.choosePlanService.getChoosePlanBuyng() !== null) {
			this.choosePlanBuyng = this.choosePlanService.getChoosePlanBuyng();
		}

		this.systemCountry = this.systemCountryService.getSystemCountry();

		this.contractForm = this.fb.group({
			termsUser: [false, Validators.requiredTrue],
			signature: [null, Validators.required],
		});

		this.contractForm.valueChanges.pipe(
			takeUntilDestroyed(this.destroyRef)
		).subscribe(({ termsUser, signature }) => {
			this.termsUser = !!termsUser;
			this.isCtaSuccess = this.termsUser && !!signature;
			this.contractSignatureDraftService.saveDraft(this.getDraftKey(), {
				termsUser: this.termsUser,
				signature: signature || null
			});
		});

		this.restoreDraftState();

		this.getDealerCountry();
	}

	get f(): { [key: string]: AbstractControl } {

		return this.contractForm.controls;
	}

	drawComplete(): void {

		const signatureCtrl = this.contractForm.get('signature');
		signatureCtrl?.markAsTouched();
		signatureCtrl?.updateValueAndValidity({ onlySelf: true });
		this.syncCtaState();
	}

	onTermsChange(): void {
		this.syncCtaState();
	}

	drawClean(): void {

		this.signaturePadCmp?.clear();
		const signatureCtrl = this.contractForm.get('signature');
		signatureCtrl?.setValue(null);
		signatureCtrl?.markAsTouched();
		signatureCtrl?.updateValueAndValidity({ onlySelf: true });
	}

	private focusTermsConsent(): boolean {
		const termsHost = this.termsConsentRef?.nativeElement;
		if (!termsHost) {
			return false;
		}

		const termsInput = termsHost.querySelector('.p-checkbox-input') as HTMLInputElement | null;
		const termsBox = termsHost.querySelector('.p-checkbox-box') as HTMLElement | null;
		if (!termsInput && !termsBox) {
			return false;
		}

		this.termsFocusActive = true;
		setTimeout(() => {
			this.termsFocusActive = false;
		}, 1300);

		termsHost.scrollIntoView({ behavior: 'smooth', block: 'center' });
		if (termsInput) {
			termsInput.focus();
		} else if (termsBox) {
			termsBox.setAttribute('tabindex', '-1');
			termsBox.focus();
		}
		return true;
	}

	getDay(): number {
		return this.dateAttachment.getDate();
	}

	getMonth(): string {

		const numberMonth = this.dateAttachment.getMonth();
		let nameMonth = '';
		switch (numberMonth) {
			case 0:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.JANUARY.UPPER.CASE');
				break;
			case 1:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.FEBRUARY.UPPER.CASE');
				break;
			case 2:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.MARCH.UPPER.CASE');
				break;
			case 3:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.APRIL.UPPER.CASE');
				break;
			case 4:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.MAY.UPPER.CASE');
				break;
			case 5:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.JUNE.UPPER.CASE');
				break;
			case 6:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.JULY.UPPER.CASE');
				break;
			case 7:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.AUGUST.UPPER.CASE');
				break;
			case 8:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.SEPTEMBER.UPPER.CASE');
				break;
			case 9:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.OCTOBER.UPPER.CASE');
				break;
			case 10:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.NOVEMBER.UPPER.CASE');
				break;
			case 11:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.DECEMBER.UPPER.CASE');
				break;
		}

		return nameMonth;
	}

	getYear(): number {
		return this.dateAttachment.getFullYear();
	}

	private restoreDraftState(): void {
		const draft = this.contractSignatureDraftService.restoreDraft(this.getDraftKey());

		if (!draft) {
			this.termsUser = false;
			this.contractForm.reset({ termsUser: false, signature: null }, { emitEvent: false });
			this.syncCtaState();
			return;
		}

		this.termsUser = draft.termsUser;
		this.contractForm.patchValue({
			termsUser: draft.termsUser,
			signature: draft.signature
		}, { emitEvent: false });
		this.syncCtaState();
	}

	private syncCtaState(): void {
		const hasSignature = !!this.contractForm.get('signature')?.value;
		this.isCtaSuccess = this.termsUser && hasSignature;
	}

	private getDraftKey(): string {
		return this.contractSignatureDraftService.buildAttachmentDraftKey('attachment-two', this.saleChannel, this.vinValidate?.vin || '');
	}

	buttonSave() {

		this.submitted = true;
		this.contractForm.markAllAsTouched();
		this.contractForm.updateValueAndValidity();

		if (this.contractForm.invalid) {
			if (this.f['termsUser']?.errors) {
				const focusedTerms = this.focusTermsConsent();
				if (!focusedTerms && this.f['signature']?.errors) {
					setTimeout(() => this.signaturePadCmp?.focusCanvas());
				}
				return;
			}

			if (this.f['signature']?.errors) {
				setTimeout(() => this.signaturePadCmp?.focusCanvas());
			}
			return;
		}

		this.setSignatureImgBase64();
		console.log('>> fim de setSignatureImgBase64');

		if (!this.signatureImgBase64) {
			this.contractForm.get('signature')?.setErrors({ required: true });
			setTimeout(() => this.signaturePadCmp?.focusCanvas());
			return;
		}

		this.save();
	}

	buttonComeBack() {
		this.router.navigate(['contract-summary', this.saleChannel], { skipLocationChange: true, replaceUrl: false });
	}

	save(): void {

		console.log('>> inicio de save');

		this.isSubmitted = true;
		this.blockedUi = true;

		this.contractSummaryService.save(this.setContractFull(), this.setDealerLanguage())
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((response: ContractFull) => {
				console.log('>> Requisição de salvar contrato finalizada!', response.contractId);
				this.messageService.add({
					severity: 'success',
					summary: this.translateService.instant('TRANSLATE.SUCCESS'),
					detail: this.translateService.instant('TRANSLATE.CONTRACT.CREATED.SUCCESSFULL')
				});

				this.vinValidate = new VinValidate();
				this.choosePlanBuyng = new ChoosePlanBuyng();
				this.clientData = new ClientData();
				this.contractSummary = new ContractSummary();

				this.contractIdDownload = response.contractId;
				this.isContractCreated = true;
				this.blockedUi = false;
				this.contractSignatureDraftService.clearDraft(this.getDraftKey());
				this.lang = "";
			}, (error) => {

				this.isContractCreated = false;
				this.isSubmitted = false;
				this.blockedUi = false;

				const friendlyError = this.contractSummaryService.getContractSaveFriendlyError(error);
				if (friendlyError) {
					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: friendlyError
					});
					return;
				}

				if (this.isApsRetryExhausted(error)) {
					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: 'Falha temporaria de autorizacao APS. A tentativa automatica nao conseguiu concluir. Tente novamente em instantes.'
					});
					return;
				}

				switch (error.status) {

					case 401:

						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
						});
						break;

					case 400:
					case 404:
					case 500:
						{
							const mappedError = this.validationErrorMapperService.mapBackendErrorToI18nKey(error);
							const errorKey = mappedError.key.startsWith('TRANSLATE.ERROR.CEP.')
								? 'TRANSLATE.CONTRACT.CANNOT.BE.SAVE.CONTACT.ADM'
								: mappedError.key;
							this.messageService.add({
								severity: 'error',
								summary: this.translateService.instant('TRANSLATE.ERROR'),
								detail: this.translateService.instant(errorKey)
							});
							break;
						}

					case 504:

						this.messageService.add({
							severity: 'error',
							summary: 'Erro 504 -> timeout',
							detail: 'Erro 504 -> timeout'
						});
						break;

					default:

						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
						});
						break;
				}

			});
	}

	private isApsRetryExhausted(error: unknown): boolean {
		return Boolean((error as { apsRetryExhausted?: boolean })?.apsRetryExhausted);
	}

	private setContractFull(): ContractFull {

		console.log('>> inicio de setContractFull');
		const contractFull = new ContractFull();
		contractFull.country = this.systemCountry;
		contractFull.operation = OperationCode.CAR;
		contractFull.status = ConstantsFordProtect.CONTRACT_ATIVO_CODE;
		contractFull.user = ConstantsFordProtect.EMISSION_USER;
		contractFull.saleDate = new Date();
		contractFull.startDate = this.contractStartDate();
		contractFull.saleType = SalesTypeFordEnum.RETAIL_CODE;
		contractFull.finalPrice = this.choosePlanBuyng.totalPrice;

		if (this.vinValidate.odometro !== null) {
			contractFull.odometerStart = this.vinValidate.odometro;
		} else {
			contractFull.odometerStart = 1;
		}

		console.log('>> VIN');
		contractFull.vin.vin = this.vinValidate.vin;
		contractFull.vin.cvvgDate = this.contractStartDate();
		contractFull.vin.factoryWarrantyEndDate = null;

		contractFull.vin.idVinCategoryVersion = this.vinValidate.idVinCategoryVersion;

		console.log('>> Payment');
		contractFull.payments.method = "DEALER";
		contractFull.payments.modality = this.contractSummary.formaPayment.value;
		contractFull.payments.parcels = this.contractSummary.prazoPayment.value;
		contractFull.payments.totalValue = this.choosePlanBuyng.totalPrice

		console.log('>> Files');
		// attachment-two = "Sem o Carro" (NÃO pagando junto com o veículo)
		// Documento correto: TERMO DE CONTRATAÇÃO – SEGURO GARANTIA ESTENDIDA REDUZIDA (PURCHASE_TERM)
		contractFull.files.type = FileType.PURCHASE_TERM;
		contractFull.files.name = FileType.PURCHASE_TERM_NAME;
		contractFull.files.description = FileType.PURCHASE_TERM_DESCRIPTION;
		contractFull.files.fileReference = FileType.PURCHASE_TERM.toString();
		contractFull.files.file = this.signatureImgBase64;

		console.log('>> Dealers');
		contractFull.dealers.code = this.contractSummary.dealer.dealerCode;
		contractFull.dealers.pAndAC = this.contractSummary.dealer.pandAC;
		contractFull.dealers.divisionCode = this.contractSummary.dealer.divisionCode;
		contractFull.dealers.oldRegionCode = this.contractSummary.dealer.oldRegionCode;
		contractFull.dealers.geoSalesCode = this.contractSummary.dealer.geoSalesCode;
		contractFull.dealers.subCode = this.contractSummary.dealer.dealerSubCode;
		contractFull.dealers.name = this.contractSummary.dealer.dealerName;
		contractFull.dealers.email = this.contractSummary.dealer.dealerEmail;
		contractFull.dealers.phone = this.contractSummary.dealer.dealerPhone;

		console.log('>> Sellers');
		contractFull.sellers.code = this.contractSummary.dealerSeller.sellerCode;
		contractFull.sellers.name = this.contractSummary.dealerSeller.name;
		contractFull.sellers.phone = this.contractSummary.dealerSeller.phone;
		contractFull.sellers.email = this.contractSummary.dealerSeller.email;

		console.log('>> Customers');
		contractFull.customers.country = this.clientData.countryCode;

		contractFull.customers.documentType =  nationalId(this.clientData.nationalId, this.systemCountry);
		contractFull.customers.documentNumber = this.clientData.nationalId;
		contractFull.customers.name = this.clientData.firstname;
		contractFull.customers.phone = this.clientData.phone1;
		contractFull.customers.email = this.clientData.email1;
		contractFull.customers.lastName = this.clientData.lastname;
		contractFull.customers.cep = this.clientData.zip;
		contractFull.customers.streetName = this.clientData.address;
		contractFull.customers.streetNumber = this.clientData.number;
		contractFull.customers.streetComplement = this.clientData.complement;
		contractFull.customers.district = this.clientData.district;
		contractFull.customers.state = this.clientData.stateCode;
		contractFull.customers.city = this.clientData.city;
		contractFull.customers.customerSocialName = this.clientData.customerSocialName;

		if (this.systemCountry === ConstantsFordProtect.BRA && this.clientData.birthDate) {
			contractFull.customers.birthDate = this.clientData.birthDate;
		}

		console.log('>> choosePlanBuyng.extension');
		if (this.choosePlanBuyng.extension.coverageCategoryId > 0 && this.choosePlanBuyng.extension.productId > 0) {
			const productFullExtension = new ProductFull();
			productFullExtension.coverageId = this.choosePlanBuyng.extension.coverageCategoryId;
			productFullExtension.productId = this.choosePlanBuyng.extension.productId;
			productFullExtension.productPolicyId = this.choosePlanBuyng.extension.productPolicyKey;
			productFullExtension.priceBookId = this.choosePlanBuyng.extension.pricebookKey;
			contractFull.products.push(productFullExtension);
		}

		console.log('>> choosePlanBuyng.maintenance');
		if (this.choosePlanBuyng.maintenance.coverageCategoryId > 0 && this.choosePlanBuyng.maintenance.productId > 0) {
			const productFullMaintenance = new ProductFull();
			productFullMaintenance.coverageId = this.choosePlanBuyng.maintenance.coverageCategoryId;
			productFullMaintenance.productId = this.choosePlanBuyng.maintenance.productId;
			productFullMaintenance.productPolicyId = this.choosePlanBuyng.extension.productPolicyKey;
			productFullMaintenance.priceBookId = this.choosePlanBuyng.extension.pricebookKey;
			productFullMaintenance.productPolicyId = this.choosePlanBuyng.maintenance.productPolicyKey;
			productFullMaintenance.priceBookId = this.choosePlanBuyng.maintenance.pricebookKey;
			contractFull.products.push(productFullMaintenance);
		}

		console.log('>> choosePlanBuyng.part');
		if (this.choosePlanBuyng.part.coverageCategoryId > 0 && this.choosePlanBuyng.part.productId > 0) {
			const productFullPart = new ProductFull();
			productFullPart.coverageId = this.choosePlanBuyng.part.coverageCategoryId;
			productFullPart.productId = this.choosePlanBuyng.part.productId;
			productFullPart.productPolicyId = this.choosePlanBuyng.part.productPolicyKey;
			productFullPart.priceBookId = this.choosePlanBuyng.part.pricebookKey;
			contractFull.products.push(productFullPart);
		}

		console.log('>> fim de setContractFull');
		return contractFull;
	}

	contractStartDate(): Date {
		let date = new Date();
		if (this.vinValidate.warrantyStartDate !== null) {
			date = this.vinValidate.warrantyStartDate;
		}
		return date;
	}

	setSignatureImgBase64(): void {
		this.signatureImgBase64 = this.imageService.getSignatureDataUrl(this.signaturePadCmp!);
	}

	/*
	 * Consulta no backend o país do dealer
	 */
	getDealerCountry() {
		this.dealerService.getDealerCountry()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (response) => {
					this.dealerCountry = response;
					this.i18nBroker.getDealerLanguageByCountry(this.dealerCountry?.countryAlternative || 'BR');
				},
				error: (error) => {

					switch (error.status) {

						case 401:

							this.messageService.add({
								severity: 'error',
								summary: this.translateService.instant('TRANSLATE.ERROR'),
								detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
							});
							break;

						case 400:
						case 404:
							// case 500:

							// PARA ESSE CASO UMA MENSAGEM DE ERRO SERÁ IMPLEMENTADA 
							// this.messageService.add({
							//   severity: 'error',
							//   summary: this.translateService.instant('TRANSLATE.ERROR'),
							//   detail: this.translateService.instant('TRANSLATE.ERROR.NO.RESULTS.FOUND.FOR.DEALER.COUNTRY.SEARCH')
							// });
							break;

						default:

							this.messageService.add({
								severity: 'error',
								summary: this.translateService.instant('TRANSLATE.ERROR'),
								detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
							});
							break;
					}

				}
			});
	}

	/*
	 * Recupera o pais do usuario logado pelo token de acesso;
	 * Caso não encontre, recupera o país do SITECODE;
	 * Em ambos os casos, faz o parse do país do dealer com a tradução oficial (pt-BR, es-CL, es-CO etc)
	 */
	setDealerLanguage() {
		if (this.systemCountry !== null) {
			this.lang = this.i18nBroker.getDealerLanguageByCountry(this.systemCountry);
			console.log('>> inicio setDealerLanguage');
		} else {
			this.lang = this.i18nBroker.getDealerLanguageByCountry(this.dealerCountry.countryAlternative);
		}
		console.log('>> fim setDealerLanguage');
		return this.lang;
	}

	downloadPDF() {

		if (this.isDownloadingContract) {
			return;
		}

		this.isDownloadingContract = true;
		this.contractService.downloadFile(this.contractIdDownload)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((response: DownloadFile) => {

				const source = response.pdfByteArray;
				const link = document.createElement("a");

				link.href = source;
				link.download = FileType.CONTRACT_TERMS_NAME;
				link.click();

				this.isDownloadingContract = false;
			}, (error) => {

				this.isDownloadingContract = false;
				switch (error.status) {

					case 401:

						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
						});
						break;

					case 400:
					case 404:
					case 500:

						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.FILE.NOT.FOUND')
						});
						break;

					default:

						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
						});
						break;
				}

			});
	}

}
